import type { RegistrationCommandType } from "./use-registration-command";

/**
 * The toast copy for a finished registration command. One hook serves both
 * directions, so an undifferentiated "your registration has been updated"
 * used to greet a withdrawal as if it were a signup — the one moment that
 * must say what just happened.
 */
export function registrationSuccessMessage(
  type: RegistrationCommandType,
): string {
  return type === "WITHDRAW_FROM_EVENT"
    ? "You've withdrawn from this event."
    : "Done! Your registration has been updated.";
}

/**
 * What the trigger writes when the offered code does not match, verbatim
 * (firestore_registration_repository.py). A wrong code, an absent one, a
 * non-string one and a blank stored secret all end here, so a refused person
 * cannot tell which of those happened.
 *
 * Exported for command-feedback.test.ts, which reads the Python source and
 * fails when the two copies drift. Nothing else in the app matches on it.
 */
export const SECRET_REFUSAL = "Registration requires the event's secret";

/**
 * The toast copy for a failed registration command.
 *
 * Command errors are trigger-side strings, and most of them are already
 * addressed to the person. The secret refusal is not: it names an internal
 * precondition, gives no next step, and reads as though nothing was typed.
 * It is replaced here rather than in the trigger, which keeps the single
 * message that every refused offering produces.
 *
 * Whether an event asks for a code at all is public: `registrationSecretEnabled`
 * is on the public event document and is what renders the field. The single
 * string buys one thing, that a wrong code cannot be told from a missing one.
 */
export function registrationFailureMessage(error: string): string {
  if (error.trim() === SECRET_REFUSAL) {
    return "That code was not accepted. Check it with the organisers and try again.";
  }
  return error;
}
